import { useEffect, useId, useRef, useState } from 'react';

/**
 * GEN-04-06 — the explanation of a page or window lives behind a small '?'
 * beside its title instead of in a sentence under it. Hover, keyboard focus or
 * a tap shows it in a dark bubble (max 340 px wide); Escape or a tap outside
 * closes it. The text is wired to the button with aria-describedby.
 */
export function TitleHelp({ children, label = 'Help' }: { children: React.ReactNode; label?: string }) {
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hover || focus || pinned;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setHover(false);
        setFocus(false);
        setPinned(false);
      }
    };
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setPinned(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  return (
    <span
      ref={root}
      className="relative inline-flex align-middle"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="title-help"
    >
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        aria-expanded={open}
        onClick={() => setPinned((v) => !v)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        className="flex h-5 w-5 items-center justify-center rounded-full border border-[#9CA3AF] text-[11px] font-semibold leading-none text-[#6B7280] hover:border-[#313131] hover:text-[#313131] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF8189]"
        data-testid="title-help-button"
      >
        ?
      </button>
      <span
        id={id}
        role="tooltip"
        hidden={!open}
        className="absolute left-0 top-full z-50 mt-1.5 w-max max-w-[min(340px,calc(100vw-2rem))] whitespace-normal rounded-lg bg-[#313131] px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed text-white shadow-lg"
        data-testid="title-help-bubble"
      >
        {children}
      </span>
    </span>
  );
}
